import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cargarMiSetup } from '@/lib/topeComerciante';
import { fetchJson, API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, FileText, DollarSign, Loader2, Plus, CheckCircle2, AlertTriangle, Search,
} from 'lucide-react';
import { StatCard } from '@/components/selletrans/comunes';
import ModalCobertura from '@/components/selletrans/ModalCobertura';
import ModalFoto from '@/components/selletrans/ModalFoto';
import ModalReclamo from '@/components/selletrans/ModalReclamo';
import PanelCodigos from '@/components/selletrans/PanelCodigos';
import { TablaCoberturas, TablaReclamos } from '@/components/selletrans/Tablas';

/**
 * Tablero de SelleTrans: las coberturas del comerciante, sus reclamos y, para la
 * oficina, los codigos de aprobacion. Cada ventana (cobertura, foto, reclamo) y
 * cada pestana vive en components/selletrans con su propio estado; aca queda la
 * carga y que esta abierto.
 */
export default function SelleTransDashboardPage() {
  const { token } = useAuth();

  const [tab, setTab] = useState('policies');
  const [cfg, setCfg] = useState(null);
  const [reports, setReports] = useState([]);
  const [claims, setClaims] = useState([]);
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [q, setQ] = useState('');
  const [banner, setBanner] = useState(null);
  const [cotizando, setCotizando] = useState(false);
  const [fotoFor, setFotoFor] = useState(null);
  const [claimFor, setClaimFor] = useState(null);
  // El tope propio del comerciante (setup), para el aviso de la cotizacion.
  const [miSetup, setMiSetup] = useState(null);
  useEffect(() => { cargarMiSetup(token).then(setMiSetup); }, [token]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, cl] = await Promise.all([
        fetchJson('/insurance/selletrans/config'),
        fetchJson('/insurance/selletrans/reports?page_size=200', { token }),
        fetchJson('/insurance/selletrans/claims', { token }),
      ]);
      setCfg(c);
      setReports(r.records || []);
      setClaims(cl.records || []);
      setLoadError('');
      // La pestana de codigos aparece solo si la lista responde: el servidor da 403 al resto.
      try {
        setCodes((await fetchJson('/insurance/selletrans/override/list', { token })).records || []);
      } catch {
        setCodes(null);
      }
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const bajarReporte = async () => {
    setBanner(null);
    try {
      const r = await fetch(`${API}/insurance/shipment-report.csv?days=90`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('The report could not be downloaded.');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipment_report_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
  };

  // Lo que pasa al terminar bien una ventana: se cierra, se avisa y se recarga.
  const listo = (cerrar) => async (texto) => {
    cerrar();
    setBanner({ type: 'ok', text: texto });
    await refresh();
  };

  if (loadError && !cfg) {
    return <p className="text-sm text-red-600 py-16 text-center" data-testid="selletrans-error">{loadError}</p>;
  }
  if (loading && !cfg) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>;
  }

  const filteredReports = reports.filter(r => !q ||
    r.verification_number?.toLowerCase().includes(q.toLowerCase()) ||
    r.tracking_number?.toLowerCase().includes(q.toLowerCase()) ||
    r.destination_zip?.includes(q));

  // Lo que pago el comerciante. Las polizas de antes del 9-oct no se cobraron.
  const totalPremium = reports.reduce((s, r) => s + (r.status !== 'void' && r.merchant_charge ? r.merchant_charge.amount : 0), 0);
  const totalCoverage = reports.reduce((s, r) => s + (r.coverage_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="selletrans-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">SelleSend (Parcel Risk)</h1>
            <p className="text-sm text-slate-500">Add coverage, view your coverages, and file claims.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Reporte por envio que pidio Mike: el comerciante baja lo suyo; el
              administrador, el maestro con todos los comerciantes. */}
          <Button variant="outline" onClick={bajarReporte} data-testid="shipment-report-btn">
            <FileText className="w-4 h-4 mr-2" />{codes ? 'Master report (CSV)' : 'Shipment report (CSV)'}
          </Button>
          <Button onClick={() => { setBanner(null); setCotizando(true); }} className="bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-2" />Add coverage
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Coverages" value={reports.length} icon={FileText} tone="purple" testid="stat-policies" />
        <StatCard label="Total coverage" value={`$${totalCoverage.toLocaleString()}`} icon={ShieldCheck} tone="emerald" testid="stat-coverage" />
        <StatCard label="Total premium" value={`$${totalPremium.toFixed(2)}`} icon={DollarSign} tone="blue" testid="stat-premium" />
        <StatCard label="Claims filed" value={claims.length} icon={AlertTriangle} tone="amber" testid="stat-claims" />
      </div>

      {loadError && (
        <p className="text-sm text-red-600" data-testid="selletrans-reload-error">{loadError}</p>
      )}
      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="dash-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          {banner.text}
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800" data-testid="dash-tabs">
        {[
          { id: 'policies', label: `Coverages (${reports.length})` },
          { id: 'claims',   label: `Claims (${claims.length})` },
          ...(codes ? [{ id: 'codes', label: `Approval codes (${codes.filter(c => c.active).length} active)` }] : []),
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 ${tab === t.id ? 'border-purple-500 text-purple-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`dash-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'policies' && (
        <div className="space-y-3">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by VN, tracking, or ZIP"
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
              data-testid="policy-search" />
          </div>
          <TablaCoberturas reports={filteredReports} token={token} onPhoto={setFotoFor} onClaim={setClaimFor} />
        </div>
      )}

      {tab === 'claims' && <TablaReclamos claims={claims} />}

      {tab === 'codes' && codes && <PanelCodigos codes={codes} token={token} onChange={refresh} />}

      {cotizando && (
        <ModalCobertura cfg={cfg} token={token} setup={miSetup}
          onClose={() => setCotizando(false)} onIssued={listo(() => setCotizando(false))} />
      )}
      {fotoFor && (
        <ModalFoto poliza={fotoFor} token={token}
          onClose={() => setFotoFor(null)} onStored={listo(() => setFotoFor(null))} />
      )}
      {claimFor && (
        <ModalReclamo poliza={claimFor} token={token}
          onClose={() => setClaimFor(null)} onFiled={listo(() => setClaimFor(null))} />
      )}
    </div>
  );
}
